import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Collapse,
  Button,
  Chip,
  useTheme,
  alpha,
  useMediaQuery,
  Fade,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Psychology as PsychologyIcon,
  AutoAwesome as AutoAwesomeIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  Info as InfoIcon,
  FilterList as FilterListIcon,
  Code as CodeIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Terminal as TerminalIcon,
  InsertDriveFile as FileIcon,
  AccountTree as RouterIcon,
  Lightbulb as PlannerIcon,
  PlayArrow as ExecutorIcon,
  Refresh as ReplannerIcon,
  AutoFixHigh as SynthesizerIcon,
  Verified as VerifiedIcon,
  VerifiedUser as VerifierIcon,
  FormatAlignLeft as FormatterIcon,
  EditNote as EditGeneratorIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ContractDocumentChip from './ContractDocumentChip';

/**
 * Legal-friendly node name fallbacks
 */
const NODE_DISPLAY_NAMES = {
  router: 'Understanding Query',
  planner: 'Planning Search Strategy',
  executor: 'Searching Documents',
  replanner: 'Refining Search',
  synthesizer: 'Generating Response',
  verifier: 'Verifying Information',
  formatter: 'Formatting Answer',
  edit_generator: 'Editing Document',
  chitchat_handler: 'Generating Greeting Response',
  out_of_scope_handler: 'Out of Scope',
};

const NODE_ICONS = {
  router: RouterIcon,
  planner: PlannerIcon,
  executor: ExecutorIcon,
  replanner: ReplannerIcon,
  synthesizer: SynthesizerIcon,
  verifier: VerifierIcon,
  formatter: FormatterIcon,
  edit_generator: EditGeneratorIcon,
};

const TOOL_ICONS = {
  decomposition: PsychologyIcon,
  retrieval: SearchIcon,
  retrieval_titles: SearchIcon,
  rerank: AutoAwesomeIcon,
  grading: AssessmentIcon,
  synthesis: DescriptionIcon,
  detect_intent: InfoIcon,
  search_by_metadata: FilterListIcon,
  get_full_document: DescriptionIcon,
  resume_document: DescriptionIcon,
  fetch_by_doc_entity: CodeIcon,
  get_related_documents: SearchIcon,
  resolve_references: InfoIcon,
  document_processor_tool: DescriptionIcon,
  search_documents: SearchIcon,
  keyword_search: SearchIcon,
  get_document_article: DescriptionIcon,
  check_document_status: VerifiedIcon,
  vector_search: SearchIcon,
  bash: TerminalIcon,
  script: CodeIcon,
  file_create: FileIcon,
};

/**
 * Merge and sort execution steps chronologically
 */
const mergeExecutionSteps = (message) => {
  const steps = [];

  if (message.node_executions && message.node_executions.length > 0) {
    message.node_executions.forEach((node) => {
      steps.push({
        id: `node-${node.node_name}`,
        type: 'node',
        name: NODE_DISPLAY_NAMES[node.node_name] || node.node_name,
        nodeName: node.node_name,
        status: node.status || 'completed',
        timestamp: node.started_at || Date.now(),
        data: node,
      });
    });
  }

  if (message.tool_executions && message.tool_executions.length > 0) {
    message.tool_executions.forEach((tool) => {
      steps.push({
        id: tool.event_id || `tool-${tool.tool_name}`,
        type: 'tool',
        name: tool.tool_name,
        status: tool.status || 'pending',
        timestamp: tool.started_at || Date.now(),
        result: tool.result,
        sources: tool.sources,
        error: tool.error,
        progress: tool.progress,
        data: tool,
      });
    });
  } else {
    const completedTools = message.completedTools || [];
    const activeTools = message.activeTools || [];
    const sources = message.sources || [];

    completedTools.forEach((toolName, index) => {
      const toolData = {
        id: `completed-${toolName}-${index}`,
        type: 'tool',
        name: toolName,
        status: 'completed',
        timestamp: Date.now() - (completedTools.length - index) * 1000,
        data: {},
      };

      if (
        ['vector_search', 'retrieval', 'search_documents', 'search_by_metadata', 'keyword_search'].includes(toolName) &&
        sources.length > 0
      ) {
        toolData.sources = sources;
        toolData.result = { documents: sources };
      }

      steps.push(toolData);
    });

    activeTools.forEach((toolName, index) => {
      steps.push({
        id: `active-${toolName}-${index}`,
        type: 'tool',
        name: toolName,
        status: 'in_progress',
        timestamp: Date.now(),
        data: {},
      });
    });
  }

  return steps.sort((a, b) => {
    const timeA = a.timestamp || '';
    const timeB = b.timestamp || '';
    if (timeA < timeB) return -1;
    if (timeA > timeB) return 1;
    return 0;
  });
};

/**
 * Document status content renderer
 */
const DocumentStatusContent = ({ statuses, theme, t }) => {
  const [showAll, setShowAll] = useState(false);
  const maxVisible = 4;
  const statusEntries = Object.entries(statuses || {});
  if (statusEntries.length === 0) return null;

  const visibleEntries = showAll ? statusEntries : statusEntries.slice(0, maxVisible);
  const remaining = statusEntries.length - maxVisible;

  return (
    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
      {visibleEntries.map(([docId, status]) => {
        const isCaseLaw = status.doc_type === 'case-law';
        const isActive = status.is_active;
        const isFinal = status.is_final_judgment;
        const abrogatedBy = status.abrogated_by;

        let statusLabel, statusColor;
        if (isCaseLaw) {
          statusLabel = isFinal ? t('final', 'Final') : t('pending', 'Pending');
          statusColor = isFinal ? theme.palette.success.main : theme.palette.warning.main;
        } else {
          statusLabel = isActive ? t('in_force', 'In force') : t('repealed', 'Repealed');
          statusColor = isActive ? theme.palette.success.main : theme.palette.error.main;
        }

        const title = status.title || (docId.length > 8 ? `${docId.substring(0, 8)}…` : docId);
        const shortTitle = title.length > 40 ? `${title.substring(0, 40)}…` : title;
        const typeLabel = isCaseLaw ? 'CAS' : 'LAW';

        return (
          <Chip
            key={docId}
            size="small"
            sx={{
              height: 24,
              backgroundColor: `${statusColor}10`,
              border: `1px solid ${statusColor}25`,
              borderRadius: '6px',
              '& .MuiChip-label': {
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                py: 0,
              },
            }}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: statusColor,
                    color: '#ffffff',
                    fontSize: '0.55rem',
                    fontWeight: 700,
                    px: 0.5,
                    py: 0.1,
                    borderRadius: '3px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.2px',
                    minWidth: 'fit-content',
                    flexShrink: 0,
                    lineHeight: 1.4,
                  }}
                >
                  {typeLabel}
                </Box>
                <Typography
                  component="span"
                  title={title}
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 200,
                  }}
                >
                  {shortTitle}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    color: statusColor,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {statusLabel}
                </Typography>
                {abrogatedBy && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.55rem',
                      color: theme.palette.error.main,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 80,
                    }}
                  >
                    ({abrogatedBy})
                  </Typography>
                )}
              </Box>
            }
          />
        );
      })}
      {!showAll && remaining > 0 && (
        <Button
          size="small"
          onClick={() => setShowAll(true)}
          sx={{
            minWidth: 'unset',
            height: 24,
            px: 1,
            py: 0,
            fontSize: '0.65rem',
            fontWeight: 600,
            borderRadius: '6px',
            textTransform: 'none',
            color: theme.palette.text.secondary,
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': { backgroundColor: theme.palette.action.hover },
          }}
        >
          +{remaining} {t('more', 'more')}
        </Button>
      )}
    </Box>
  );
};

/**
 * Retrieval content renderer (document chips) - display-only, no click handler
 */
const RetrievalContent = ({ documents, theme, isMobile }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const maxVisible = isMobile ? 3 : 4;

  const normalizedDocs = (Array.isArray(documents) ? documents : []).map(doc => {
    if (doc.metadata) return doc;
    if (doc.document_id || doc.id) {
      const docType = doc.doc_type || 'unknown';
      const metadata = {
        document_id: doc.document_id || doc.id,
        external_id: doc.external_id,
        doc_type: docType,
        section_title: doc.section_title,
        country_code: doc.country_code,
        source_language: doc.source_language,
        available_languages: doc.available_languages,
        source_url: doc.source_url,
        source_platform: doc.source_platform,
      };

      if (docType === 'law') {
        metadata.title = doc.title;
        metadata.doc_number = doc.doc_number;
        metadata.issue_year = doc.issue_year;
        metadata.legislation_type = doc.legislation_type;
        metadata.doc_type_label = doc.doc_type_label;
        metadata.files = doc.files;
      } else if (docType === 'case-law') {
        metadata.case_number = doc.case_number;
        metadata.court_name = doc.court_name;
        metadata.case_type = doc.case_type;
        metadata.subject_matter = doc.subject_matter;
        metadata.case_year = doc.case_year;
      }

      return {
        id: doc.id,
        metadata,
        page_content: doc.page_content,
        similarity_score: doc.similarity_score,
      };
    }
    return doc;
  });

  const visibleDocs = showAll ? normalizedDocs : normalizedDocs.slice(0, maxVisible);
  const remaining = normalizedDocs.length - maxVisible;

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {visibleDocs.map((doc, i) => (
          <ContractDocumentChip
            key={doc?.metadata?.document_id || i}
            document={doc}
            compact
          />
        ))}
      </Box>

      {!showAll && remaining > 0 && (
        <Button
          size="small"
          onClick={() => setShowAll(true)}
          sx={{
            mt: 0.75,
            fontSize: '0.7rem',
            color: theme.palette.primary.main,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          {t('more_items', '+{{count}} more', { count: remaining })}
        </Button>
      )}
    </Box>
  );
};

/**
 * Bash command content renderer
 */
const BashContent = ({ result, theme }) => {
  const { t } = useTranslation();
  const [showFullOutput, setShowFullOutput] = useState(false);

  const command = result.command || result.cmd || '';
  const output = result.output || result.stdout || result.stderr || '';
  const outputLines = output.split('\n');
  const shouldTruncate = outputLines.length > 10;
  const visibleOutput = showFullOutput ? output : outputLines.slice(0, 10).join('\n');

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          bgcolor: alpha(theme.palette.common.black, 0.85),
          p: 1.5,
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          overflow: 'auto',
        }}
      >
        {command && (
          <Box sx={{ color: '#7EE787', mb: output ? 1 : 0, lineHeight: 1.5 }}>
            {command}
          </Box>
        )}
        {output && (
          <Box sx={{ color: '#C9D1D9', opacity: 0.9, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {visibleOutput}
          </Box>
        )}
      </Box>
      {shouldTruncate && !showFullOutput && (
        <Button
          size="small"
          onClick={() => setShowFullOutput(true)}
          sx={{
            mt: 0.75,
            fontSize: '0.7rem',
            color: theme.palette.primary.main,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
          }}
        >
          {t('more_lines', '+{{count}} more lines', { count: outputLines.length - 10 })}
        </Button>
      )}
    </Box>
  );
};

/**
 * Script content renderer
 */
const ScriptContent = ({ result, theme }) => {
  const { t } = useTranslation();
  const [showFullOutput, setShowFullOutput] = useState(false);

  const scriptName = result.script_name || result.name || 'Script';
  const output = result.output || result.result || '';
  const outputLines = output.split('\n');
  const shouldTruncate = outputLines.length > 10;
  const visibleOutput = showFullOutput ? output : outputLines.slice(0, 10).join('\n');

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 1, color: theme.palette.text.secondary, fontSize: '0.75rem', fontWeight: 500 }}
      >
        {scriptName}
      </Typography>
      {output && (
        <>
          <Box
            sx={{
              bgcolor: alpha(theme.palette.common.black, 0.85),
              p: 1.5,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              color: '#C9D1D9',
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              overflow: 'auto',
            }}
          >
            {visibleOutput}
          </Box>
          {shouldTruncate && !showFullOutput && (
            <Button
              size="small"
              onClick={() => setShowFullOutput(true)}
              sx={{
                mt: 0.75,
                fontSize: '0.7rem',
                color: theme.palette.primary.main,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              {t('more_lines', '+{{count}} more lines', { count: outputLines.length - 10 })}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

/**
 * File creation content renderer
 */
const FileContent = ({ result, theme }) => {
  const filename = result.filename || result.name || 'File';
  const fileType = result.type || result.extension || '';

  return (
    <Box
      sx={{
        mt: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      }}
    >
      <FileIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
      <Typography
        variant="caption"
        sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: theme.palette.text.primary, fontWeight: 500 }}
      >
        {filename}
      </Typography>
      {fileType && (
        <Typography
          variant="caption"
          sx={{ fontSize: '0.65rem', color: theme.palette.text.secondary, textTransform: 'uppercase' }}
        >
          {fileType}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Decomposition content renderer
 */
const DecompositionContent = ({ queries, theme }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const maxVisible = 3;
  const visibleQueries = showAll ? queries : queries.slice(0, maxVisible);
  const remaining = queries.length - maxVisible;

  return (
    <Box sx={{ mt: 1 }}>
      {visibleQueries.map((query, i) => (
        <Typography
          key={i}
          variant="caption"
          sx={{
            display: 'block',
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            pl: 1.5,
            borderLeft: `2px solid ${theme.palette.divider}`,
            mb: 0.5,
            lineHeight: 1.5,
          }}
        >
          {query}
        </Typography>
      ))}
      {!showAll && remaining > 0 && (
        <Button
          size="small"
          onClick={() => setShowAll(true)}
          sx={{
            mt: 0.5,
            fontSize: '0.7rem',
            color: theme.palette.primary.main,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
          }}
        >
          {t('more_items', '+{{count}} more', { count: remaining })}
        </Button>
      )}
    </Box>
  );
};

/**
 * Contract Execution Steps Display
 * Unified vertical timeline for node executions + tool executions
 * Display-only version (no document viewer integration)
 */
const ContractExecutionStepsDisplay = ({ message, isStreaming = false }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isRTL = i18n.language === 'ar';

  const [globalExpanded, setGlobalExpanded] = useState(true);
  const [hasAutoCollapsed, setHasAutoCollapsed] = useState(false);
  const [stepExpanded, setStepExpanded] = useState({});

  const steps = useMemo(() => mergeExecutionSteps(message), [
    message.node_executions,
    message.execution_plan,
    message.tool_executions,
    message.activeTools,
    message.completedTools,
    message.sources,
  ]);

  const isAllComplete = useMemo(() => {
    if (steps.length === 0) return false;
    if (isStreaming) {
      return (message.activeTools || []).length === 0 && steps.length > 0;
    }
    return steps.every((s) => s.status === 'completed');
  }, [steps, isStreaming, message.activeTools]);

  const completedCount = steps.filter((s) => s.status === 'completed').length;

  // Auto-collapse when all complete
  useEffect(() => {
    if (isAllComplete && !hasAutoCollapsed && !isStreaming && steps.length > 0) {
      const timer = setTimeout(() => {
        setGlobalExpanded(false);
        setHasAutoCollapsed(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isAllComplete, hasAutoCollapsed, isStreaming, steps.length]);

  // Reset auto-collapse on new executions
  useEffect(() => {
    if (steps.length > 0 && !isAllComplete) {
      setHasAutoCollapsed(false);
      setGlobalExpanded(true);
    }
  }, [steps.length, isAllComplete]);

  const toggleStep = (stepId) => {
    setStepExpanded((prev) => ({
      ...prev,
      [stepId]: !prev[stepId],
    }));
  };

  const getStatusStyles = (status) => {
    const styles = {
      pending: {
        color: theme.palette.text.disabled,
        bgColor: 'transparent',
        borderColor: theme.palette.divider,
        animate: false,
      },
      started: {
        color: theme.palette.primary.main,
        bgColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        animate: true,
      },
      in_progress: {
        color: theme.palette.primary.main,
        bgColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        animate: true,
      },
      completed: {
        color: theme.palette.success.main,
        bgColor: theme.palette.success.main,
        borderColor: theme.palette.success.main,
        animate: false,
      },
      failed: {
        color: theme.palette.error.main,
        bgColor: theme.palette.error.main,
        borderColor: theme.palette.error.main,
        animate: false,
      },
    };
    return styles[status] || styles.pending;
  };

  const getStepIcon = (step) => {
    if (step.type === 'node' && step.nodeName) {
      return NODE_ICONS[step.nodeName] || CodeIcon;
    }
    if (step.type === 'tool' || step.type === 'plan_step') {
      return TOOL_ICONS[step.name] || TOOL_ICONS[step.toolName] || CodeIcon;
    }
    return CodeIcon;
  };

  const getStepDisplayName = (step) => {
    if (step.type === 'node') {
      const nodeKey = `node_${step.nodeName}`;
      return t(nodeKey, NODE_DISPLAY_NAMES[step.nodeName] || step.name);
    }

    if (step.type === 'tool') {
      const toolMessages = {
        decomposition: t('analyzing_query', 'Analyzing query'),
        detect_intent: t('understanding_intent', 'Understanding intent'),
        retrieval: t('searching_documents', 'Searching documents'),
        retrieval_titles: t('searching_titles', 'Searching titles'),
        search_by_metadata: t('filtering_documents', 'Filtering documents'),
        get_related_documents: t('finding_related', 'Finding related documents'),
        fetch_by_doc_entity: t('fetching_document', 'Fetching document'),
        get_full_document: t('loading_document', 'Loading full document'),
        grading: t('evaluating_relevance', 'Evaluating relevance'),
        rerank: t('ranking_results', 'Ranking results'),
        resume_document: t('summarizing', 'Summarizing document'),
        synthesis: t('generating_response', 'Generating response'),
        resolve_references: t('resolving_refs', 'Resolving references'),
        search_documents: t('searching_documents', 'Searching documents'),
        keyword_search: t('reference_lookup', 'Reference lookup'),
        get_document_article: t('fetching_article', 'Fetching article'),
        vector_search: t('searching_documents', 'Searching documents'),
        check_document_status: t('checking_status', 'Checking document status'),
        bash: t('execute_command', 'Execute command'),
        script: t('run_script', 'Run script'),
      };
      return toolMessages[step.name] || step.name.replace(/_/g, ' ');
    }

    return step.name;
  };

  const getCollapsedChip = (step) => {
    if (step.type === 'node') return null;
    if (step.type === 'tool') {
      if (step.name === 'bash') return 'bash';
      if (step.name === 'script') return step.result?.script_name || 'Script';
      if (step.name === 'file_create') return step.result?.filename || 'File';
    }
    return null;
  };

  const renderStepContent = (step) => {
    const { type, name, result, sources, error, data } = step;

    if (error) {
      return (
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            bgcolor: alpha(theme.palette.error.main, 0.1),
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: theme.palette.error.main, fontSize: '0.75rem', lineHeight: 1.5 }}
          >
            {error}
          </Typography>
        </Box>
      );
    }

    // Node content
    if (type === 'node') {
      const reasoning = data.reasoning;
      const resultMeta = data.result_metadata;

      if (reasoning || resultMeta) {
        return (
          <Box sx={{ mt: 1 }}>
            {reasoning && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.7rem',
                  lineHeight: 1.5,
                  display: 'block',
                  fontStyle: 'italic',
                }}
              >
                {reasoning}
              </Typography>
            )}

            {data.node_name === 'router' && resultMeta?.route && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: reasoning ? 0.5 : 0 }}>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    borderRadius: '4px',
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.65rem', fontWeight: 600, color: theme.palette.info.main }}
                  >
                    {t(`route_${resultMeta.route}`, resultMeta.route.replace(/_/g, ' '))}
                  </Typography>
                </Box>
                {resultMeta.sources_to_search?.map((src, i) => (
                  <Box
                    key={i}
                    sx={{
                      px: 0.75,
                      py: 0.25,
                      bgcolor: alpha(theme.palette.text.secondary, 0.08),
                      borderRadius: '4px',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.6rem', color: theme.palette.text.secondary }}
                    >
                      {t(`source_${src}`, src.replace(/_/g, ' '))}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {data.node_name === 'verifier' && resultMeta && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: reasoning ? 0.5 : 0 }}>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    bgcolor: alpha(
                      resultMeta.is_valid ? theme.palette.success.main : theme.palette.warning.main,
                      0.1,
                    ),
                    borderRadius: '4px',
                    border: `1px solid ${alpha(
                      resultMeta.is_valid ? theme.palette.success.main : theme.palette.warning.main,
                      0.2,
                    )}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      color: resultMeta.is_valid ? theme.palette.success.main : theme.palette.warning.main,
                    }}
                  >
                    {resultMeta.is_valid ? t('verified', 'Verified') : t('issues_found', 'Issues found')}
                  </Typography>
                </Box>
                {resultMeta.auto_corrections_count > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.6rem', color: theme.palette.text.secondary }}
                  >
                    {resultMeta.auto_corrections_count} {t('corrections', 'corrections')}
                  </Typography>
                )}
              </Box>
            )}

            {data.node_name === 'replanner' && resultMeta?.new_steps_count > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: reasoning ? 0.5 : 0 }}>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: '4px',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.65rem', fontWeight: 600, color: theme.palette.primary.main }}
                  >
                    +{resultMeta.new_steps_count} {t('new_steps', 'new steps')}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        );
      }
      return null;
    }

    // Bash content
    if (name === 'bash' && result) {
      return <BashContent result={result} theme={theme} />;
    }

    // Script content
    if (name === 'script' && result) {
      return <ScriptContent result={result} theme={theme} />;
    }

    // File content
    if (name === 'file_create' && result) {
      return <FileContent result={result} theme={theme} />;
    }

    // Retrieval content (document chips - display only)
    if (
      ['vector_search', 'retrieval', 'retrieval_titles', 'search_by_metadata', 'get_related_documents', 'search_documents', 'keyword_search'].includes(name) &&
      (result?.chunks || result?.documents || sources)
    ) {
      const documents = result?.chunks || result?.documents || sources || [];
      return (
        <RetrievalContent
          documents={documents}
          theme={theme}
          isMobile={isMobile}
        />
      );
    }

    // Get document article
    if (name === 'get_document_article' && result) {
      const articleDoc = {
        metadata: {
          document_id: result.document_id,
          doc_type: 'law',
          title: result.section_title || `Article ${result.article_number}`,
          section_title: result.section_title,
        },
        page_content: result.article_text,
      };
      return (
        <RetrievalContent
          documents={[articleDoc]}
          theme={theme}
          isMobile={isMobile}
        />
      );
    }

    // Decomposition
    if (name === 'decomposition' && result?.sub_queries) {
      return <DecompositionContent queries={result.sub_queries} theme={theme} />;
    }

    // Grading
    if (name === 'grading' && result) {
      const relevant = result.relevant_count || result.relevant || 0;
      const total = result.total_documents || result.total || 0;
      if (total > 0) {
        return (
          <Typography
            variant="caption"
            sx={{ mt: 1, display: 'block', color: theme.palette.text.secondary, fontSize: '0.75rem' }}
          >
            {relevant}/{total} {t('relevant', 'relevant')}
          </Typography>
        );
      }
    }

    // Intent detection
    if (name === 'detect_intent' && result?.intent_type) {
      return (
        <Typography
          variant="caption"
          sx={{ mt: 1, display: 'block', color: theme.palette.info.main, fontSize: '0.75rem', fontWeight: 500 }}
        >
          {result.intent_type.replace(/_/g, ' ')}
        </Typography>
      );
    }

    // Document status
    if (name === 'check_document_status' && result?.statuses) {
      return <DocumentStatusContent statuses={result.statuses} theme={theme} t={t} />;
    }

    // Generic content
    if (result && typeof result === 'object') {
      return (
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            bgcolor: alpha(theme.palette.common.black, 0.05),
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: 200,
          }}
        >
          <Typography
            component="pre"
            sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: theme.palette.text.secondary, margin: 0 }}
          >
            {JSON.stringify(result, null, 2)}
          </Typography>
        </Box>
      );
    }

    return null;
  };

  const renderStatusIndicator = (status) => {
    const styles = getStatusStyles(status);

    return (
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: styles.bgColor,
          border: status === 'pending' ? `2px solid ${styles.borderColor}` : 'none',
          flexShrink: 0,
          ...(styles.animate && {
            animation: 'statusPulse 1.5s ease-in-out infinite',
            '@keyframes statusPulse': {
              '0%, 100%': {
                transform: 'scale(1)',
                boxShadow: `0 0 0 0 ${alpha(styles.bgColor, 0.4)}`,
              },
              '50%': {
                transform: 'scale(1.2)',
                boxShadow: `0 0 0 4px ${alpha(styles.bgColor, 0)}`,
              },
            },
          }),
        }}
      >
        {status === 'completed' && (
          <CheckIcon sx={{ fontSize: 6, color: '#fff', position: 'relative', top: -2, left: -2 }} />
        )}
        {status === 'failed' && (
          <CloseIcon sx={{ fontSize: 6, color: '#fff', position: 'relative', top: -2, left: -2 }} />
        )}
      </Box>
    );
  };

  const renderStep = (step, index) => {
    const isExpanded = stepExpanded[step.id] !== undefined ? stepExpanded[step.id] : globalExpanded;
    const IconComponent = getStepIcon(step);
    const displayName = getStepDisplayName(step);
    const collapsedChip = getCollapsedChip(step);
    const statusStyles = getStatusStyles(step.status);
    const isActive = step.status === 'in_progress' || step.status === 'started';
    const hasContent = renderStepContent(step) !== null;

    return (
      <Fade in timeout={200 + index * 50} key={step.id}>
        <Box
          sx={{
            position: 'relative',
            borderBottom: index < steps.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
          }}
        >
          <Box
            onClick={() => hasContent && toggleStep(step.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1.5,
              px: 2,
              cursor: hasContent ? 'pointer' : 'default',
              transition: 'background-color 0.15s ease',
              ...(hasContent && {
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.3),
                },
              }),
            }}
          >
            {renderStatusIndicator(step.status)}

            <IconComponent
              sx={{
                fontSize: 16,
                color: statusStyles.color,
                opacity: step.status === 'pending' ? 0.5 : 1,
                flexShrink: 0,
              }}
            />

            <Typography
              variant="body2"
              sx={{
                fontWeight: isActive ? 600 : 500,
                color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
                fontSize: isMobile ? '0.8rem' : '0.85rem',
                flex: 1,
                lineHeight: 1.4,
              }}
            >
              {displayName}
            </Typography>

            {!isExpanded && collapsedChip && (
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: 0.5,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    color: theme.palette.primary.main,
                    fontFamily: collapsedChip === 'bash' ? 'monospace' : 'inherit',
                  }}
                >
                  {collapsedChip}
                </Typography>
              </Box>
            )}

            {isActive && step.progress !== undefined && step.progress > 0 && (
              <Typography
                variant="caption"
                sx={{ fontSize: '0.65rem', fontWeight: 600, color: theme.palette.primary.main }}
              >
                {Math.round(step.progress * 100)}%
              </Typography>
            )}

            {hasContent && (
              <ArrowDownIcon
                sx={{
                  fontSize: 18,
                  color: theme.palette.text.secondary,
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s ease',
                  flexShrink: 0,
                }}
              />
            )}
          </Box>

          {hasContent && (
            <Collapse in={isExpanded} timeout={250}>
              <Box sx={{ px: 2, pb: 1.5 }}>{renderStepContent(step)}</Box>
            </Collapse>
          )}
        </Box>
      </Fade>
    );
  };

  if (steps.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        mb: 2,
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      {/* Global Header */}
      <Box
        onClick={() => setGlobalExpanded(!globalExpanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          cursor: 'pointer',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: isAllComplete
            ? alpha(theme.palette.success.main, 0.05)
            : alpha(theme.palette.primary.main, 0.05),
          transition: 'background-color 0.2s ease',
          '&:hover': {
            bgcolor: isAllComplete
              ? alpha(theme.palette.success.main, 0.08)
              : alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AutoAwesomeIcon
            sx={{
              fontSize: 18,
              color: isAllComplete ? theme.palette.success.main : theme.palette.primary.main,
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: '0.85rem',
              letterSpacing: '0.01em',
            }}
          >
            {isAllComplete ? t('processing_complete', 'Processing complete') : t('processing', 'Processing')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.75rem',
              fontWeight: 500,
              opacity: 0.8,
            }}
          >
            {completedCount}/{steps.length}
          </Typography>
        </Box>

        <ArrowDownIcon
          sx={{
            fontSize: 20,
            color: theme.palette.text.secondary,
            transform: globalExpanded ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.25s ease',
          }}
        />
      </Box>

      {/* Steps List */}
      <Collapse in={globalExpanded} timeout={300}>
        <Box>{steps.map((step, index) => renderStep(step, index))}</Box>
      </Collapse>
    </Box>
  );
};

export default ContractExecutionStepsDisplay;
